import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router";
import { CATEGORY_LABELS, type Category } from "@abejon/shared";
import { useGeolocation } from "../../hooks/useGeolocation";
import { BusinessCard } from "../../components/business/BusinessCard";
import { Skeleton, SkeletonText, SkeletonCircle } from "../../components/ui/Skeleton";
import { EmptyState } from "../../components/ui/EmptyState";
import * as exploreApi from "../../api/explore.api";
import type { BusinessResult, FeedPost } from "../../api/explore.api";

const RADIUS_OPTIONS = [5, 10, 25, 50];
const ALL_CATEGORIES: Array<{ value: string; label: string }> = [
  { value: "", label: "All" },
  { value: "barber", label: CATEGORY_LABELS.barber },
  { value: "stylist", label: CATEGORY_LABELS.stylist },
  { value: "nail_tech", label: CATEGORY_LABELS.nail_tech },
];

type Tab = "businesses" | "feed";

export function ExplorePage() {
  const { latitude, longitude, error, loading, denied, requestLocation } = useGeolocation();

  // Fallback location input
  const [manualLat, setManualLat] = useState("");
  const [manualLng, setManualLng] = useState("");
  const [usingManual, setUsingManual] = useState(false);

  // Search state
  const [tab, setTab] = useState<Tab>("businesses");
  const [category, setCategory] = useState("");
  const [radius, setRadius] = useState(25);
  const [query, setQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");

  // Results
  const [businesses, setBusinesses] = useState<BusinessResult[]>([]);
  const [feedPosts, setFeedPosts] = useState<FeedPost[]>([]);
  const [loadingResults, setLoadingResults] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  // Effective coordinates
  const lat = usingManual ? parseFloat(manualLat) : latitude;
  const lng = usingManual ? parseFloat(manualLng) : longitude;
  const hasLocation = lat !== null && lng !== null && !isNaN(lat) && !isNaN(lng);

  // Request location on mount
  useEffect(() => {
    requestLocation();
  }, [requestLocation]);

  // Fetch businesses
  const fetchBusinesses = useCallback(async (pageNum: number, append: boolean) => {
    if (!hasLocation) return;
    setLoadingResults(true);
    setFetchError(false);
    try {
      const result = await exploreApi.searchBusinesses({
        lat: lat!,
        lng: lng!,
        radius,
        category: category || undefined,
        query: query || undefined,
        page: pageNum,
      });
      setBusinesses((prev) => append ? [...prev, ...result.businesses] : result.businesses);
      setHasMore(result.businesses.length >= result.limit);
      setPage(pageNum);
    } catch {
      if (!append) setFetchError(true);
    } finally {
      setLoadingResults(false);
    }
  }, [hasLocation, lat, lng, radius, category, query]);

  // Fetch feed posts
  const fetchFeed = useCallback(async (pageNum: number, append: boolean) => {
    if (!hasLocation) return;
    setLoadingResults(true);
    setFetchError(false);
    try {
      const result = await exploreApi.getExploreFeed({
        lat: lat!,
        lng: lng!,
        radius,
        page: pageNum,
      });
      setFeedPosts((prev) => append ? [...prev, ...result.posts] : result.posts);
      setHasMore(result.posts.length >= result.limit);
      setPage(pageNum);
    } catch {
      if (!append) setFetchError(true);
    } finally {
      setLoadingResults(false);
    }
  }, [hasLocation, lat, lng, radius]);

  // Refetch on filter changes
  useEffect(() => {
    if (!hasLocation) return;
    setPage(1);
    if (tab === "businesses") {
      fetchBusinesses(1, false);
    } else {
      fetchFeed(1, false);
    }
  }, [hasLocation, tab, category, radius, query, fetchBusinesses, fetchFeed]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setQuery(searchInput);
  };

  const handleManualSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (manualLat && manualLng) {
      setUsingManual(true);
    }
  };

  const loadMore = () => {
    const next = page + 1;
    if (tab === "businesses") {
      fetchBusinesses(next, true);
    } else {
      fetchFeed(next, true);
    }
  };

  // Location prompt state
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="mb-4 h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-gray-900" />
        <p className="text-sm text-gray-500">Getting your location...</p>
      </div>
    );
  }

  if (!hasLocation && !denied) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <h1 className="mb-2 text-2xl font-bold">Explore</h1>
        <p className="mb-6 text-gray-500">
          Allow location access to discover nearby businesses.
        </p>
        <button
          onClick={requestLocation}
          className="rounded-lg bg-gray-900 px-6 py-2.5 text-sm font-medium text-white hover:bg-gray-800"
        >
          Enable Location
        </button>
      </div>
    );
  }

  if (!hasLocation && denied) {
    return (
      <div className="mx-auto max-w-sm py-10">
        <h1 className="mb-2 text-2xl font-bold">Explore</h1>
        <p className="mb-6 text-sm text-gray-500">{error}</p>

        <form onSubmit={handleManualSubmit} className="space-y-3">
          <p className="text-sm font-medium text-gray-700">
            Enter coordinates manually:
          </p>
          <div className="flex gap-3">
            <input
              type="text"
              placeholder="Latitude"
              value={manualLat}
              onChange={(e) => setManualLat(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-gray-900 focus:ring-1 focus:ring-gray-900 focus:outline-none"
            />
            <input
              type="text"
              placeholder="Longitude"
              value={manualLng}
              onChange={(e) => setManualLng(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-gray-900 focus:ring-1 focus:ring-gray-900 focus:outline-none"
            />
          </div>
          <button
            type="submit"
            className="w-full rounded-lg bg-gray-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-gray-800"
          >
            Search
          </button>
        </form>

        <button
          onClick={requestLocation}
          className="mt-4 w-full text-center text-sm text-gray-500 underline hover:text-gray-700"
        >
          Try location again
        </button>
      </div>
    );
  }

  return (
    <div>
      <h1 className="mb-4 text-2xl font-bold">Explore</h1>

      {/* Search bar */}
      <form onSubmit={handleSearch} className="mb-4">
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="Search businesses..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-gray-900 focus:ring-1 focus:ring-gray-900 focus:outline-none"
          />
          <button
            type="submit"
            className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800"
          >
            Search
          </button>
        </div>
      </form>

      {/* Tabs */}
      <div className="mb-4 flex border-b border-gray-200">
        <button
          onClick={() => setTab("businesses")}
          className={`px-4 py-2 text-sm font-medium ${
            tab === "businesses"
              ? "border-b-2 border-gray-900 text-gray-900"
              : "text-gray-500 hover:text-gray-700"
          }`}
        >
          Businesses
        </button>
        <button
          onClick={() => setTab("feed")}
          className={`px-4 py-2 text-sm font-medium ${
            tab === "feed"
              ? "border-b-2 border-gray-900 text-gray-900"
              : "text-gray-500 hover:text-gray-700"
          }`}
        >
          Posts
        </button>
      </div>

      {/* Filters row */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        {/* Category tabs */}
        {tab === "businesses" && (
          <div className="flex flex-wrap gap-1.5">
            {ALL_CATEGORIES.map((cat) => (
              <button
                key={cat.value}
                onClick={() => setCategory(cat.value)}
                className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                  category === cat.value
                    ? "bg-gray-900 text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        )}

        {/* Radius selector */}
        <select
          value={radius}
          onChange={(e) => setRadius(Number(e.target.value))}
          className="rounded-lg border border-gray-300 px-2 py-1 text-xs focus:border-gray-900 focus:ring-1 focus:ring-gray-900 focus:outline-none"
        >
          {RADIUS_OPTIONS.map((r) => (
            <option key={r} value={r}>
              {r} miles
            </option>
          ))}
        </select>
      </div>

      {/* Results */}
      {fetchError ? (
        <div className="py-12 text-center">
          <p className="mb-3 text-sm text-gray-500">Failed to load results.</p>
          <button
            onClick={() => tab === "businesses" ? fetchBusinesses(1, false) : fetchFeed(1, false)}
            className="rounded-md bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2"
          >
            Retry
          </button>
        </div>
      ) : loadingResults && businesses.length === 0 && feedPosts.length === 0 ? (
        <div className="space-y-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="flex gap-4 rounded-lg border border-gray-200 p-4">
              <SkeletonCircle className="h-14 w-14" />
              <div className="flex-1 space-y-2">
                <SkeletonText className="h-4 w-36" />
                <SkeletonText className="h-3 w-24" />
                <SkeletonText className="h-3 w-full" />
              </div>
            </div>
          ))}
        </div>
      ) : tab === "businesses" ? (
        <div>
          {businesses.length === 0 ? (
            <EmptyState
              title="No businesses found"
              description="Try expanding your search radius or changing category."
            />
          ) : (
            <div className="space-y-3">
              {businesses.map((biz) => (
                <BusinessCard
                  key={biz.id}
                  slug={biz.slug}
                  displayName={biz.displayName}
                  category={biz.category}
                  avatarUrl={biz.avatarUrl}
                  city={biz.city}
                  state={biz.state}
                  distance={biz.distance}
                  bio={biz.bio}
                />
              ))}
            </div>
          )}
        </div>
      ) : (
        <div>
          {feedPosts.length === 0 ? (
            <EmptyState
              title="No posts found"
              description="No posts from nearby businesses yet."
            />
          ) : (
            <div className="grid grid-cols-3 gap-1">
              {feedPosts.map((post) => (
                <Link
                  key={post.id}
                  to={`/posts/${post.id}`}
                  className="relative aspect-square overflow-hidden rounded-sm bg-gray-100"
                >
                  {post.imageUrl ? (
                    <img
                      src={post.imageUrl}
                      alt={post.caption ?? ""}
                      className="h-full w-full object-cover transition-opacity hover:opacity-90"
                      loading="lazy"
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center text-xs text-gray-400">
                      No image
                    </div>
                  )}
                </Link>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Load more */}
      {hasMore && !loadingResults && (
        <div className="mt-6 text-center">
          <button
            onClick={loadMore}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Load more
          </button>
        </div>
      )}

      {loadingResults && (businesses.length > 0 || feedPosts.length > 0) && (
        <div className="mt-4 flex justify-center">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-gray-900" />
        </div>
      )}
    </div>
  );
}
